import { NextrayModel, SaveHandlerBase, ToolState, ToolToSave, ToolType, ToolUtils, ViewType } from "@ortho-next/nextray-core";
import { bindedModel } from "../Models/BindedModel";
import { Context } from "../States/Context";
import { Main } from "./Main";
import { Tools } from "./Tools";

/**
 * Contains data to restore fitbone app.
 */
export interface TemplateModel {
	state: string;
	bindedModel: string;
	appStateModel: string;
	ap: TemplateToolState;
	lt: TemplateToolState;
}

/**
 * Contains data to restore a single scene.
 */
export interface TemplateToolState {
	tools: ToolState[];
}

/**
 * Manages the save and restore.
 */
export class SaveHandler extends SaveHandlerBase {
	protected declare _main: Main; //override
	protected declare _context: Context; //override
	// private _model: TemplateModel;
	protected override readonly cacheTools: ToolType[] = [
		ToolType.plane,
		ToolType.line,
		ToolType.goniometer,
		ToolType.mechanicalAxis,
		ToolType.contralateralMechanicalAxis
	];

	protected _isFirstBlockingScrewRestore = [true, true];

	constructor(main: Main, context: Context) {
		super(main, context);
	}

	protected override onBeforeAppModelStringfy = (appModel: any) => { // TODO fix in package
		appModel.printResult = undefined;
		appModel.printMeasures = undefined;
		appModel.isBlockingScrewSelectedAP = undefined;
		appModel.isBlockingScrewSelectedLT = undefined;
	}

	protected override  onBeforeBindedModelStringfy = (bindedModelCloned: any) => { // TODO fix in package
		bindedModelCloned.isCropPolygonToolVisible = false;
	}

	/**
	 * Inits and restore tools.
	 */
	public override async restoreData(restoreData: TemplateModel, cacheData: NextrayModel): Promise<void> {
		this.mergeData(cacheData);
		await super.restoreData(restoreData, cacheData);
	}

	protected override initMesh(tools: Tools, meshType: ToolType, viewType: ViewType, meshData: { [x: string]: any }): ToolToSave | undefined {
		switch (meshType) {
			case ToolType.referencePoint:
				return viewType === ViewType.AP ? this._main.toolsInit.initReferencePointAP() : this._main.toolsInit.initReferencePointLT();
			case ToolType.osteotomy:
				return viewType === ViewType.AP ? this._main.toolsInit.initOsteotomyAP() : this._main.toolsInit.initOsteotomyLT();
			case ToolType.EOCCrop:
				return this._main.toolsInit.initEoCPlaneTool(viewType);
			case ToolType.grid:
				return tools.grid;
			case ToolType.insertionPoint:
				return this._main.toolsInit.initInsertionPoint(viewType);
			case ToolType.diaphysisAnatomicalAxis:
				return viewType === ViewType.LT ? this._main.toolsInit.initDiaphysisAA() : undefined;
			case ToolType.fitBone:
				return this._main.toolsInit.initFitBone(viewType);
			case ToolType.blockingScrew:
				// TODO remove this hack to fix blocking screw restore in next release
				if (this._isFirstBlockingScrewRestore[viewType]) {
					const blockingScrewMaskKey = viewType === ViewType.AP ? 'blockingScrewMaskAP' : 'blockingScrewMaskLT';
					bindedModel[blockingScrewMaskKey] = 0;
					this._isFirstBlockingScrewRestore[viewType] = false;
				}
				return this._main.toolsInit.initBlockingScrew(tools, false);
		}
		return super.initMesh(tools, meshType, viewType, meshData);
	}

	protected override  updateTools(viewType: ViewType): void {
		const toolsToSave = viewType === ViewType.AP ? this._model.ap.tools : this._model.lt.tools;
		const tools = this._main.getTools(viewType);

		for (const tool of [ // order is the priority. Puo' essere automatizzato
			tools.plane,
			...tools.lines,
			...tools.goniometers,
			tools.mechanicalAxis,
			tools.contralateralMechanicalAxis,
			tools.referencePoint,
			tools.grid,
			tools.insertionPointManager,
			tools.diaphysisAA,
			tools.osteotomy,
			tools.EoCPlane,
			tools.fitBoneManager,
			...tools.blockingScrews.filter(x => x.visible)
		]) {
			const toolToSave = ToolUtils.getStateToRestore(tool);
			toolToSave && toolsToSave.push(toolToSave);
		}
	}

	private mergeData(cacheData: NextrayModel): void {
		const cachedAppModel = JSON.parse(cacheData.appStateModel!);

		for (const key in cachedAppModel) {
			(this._main.deformityModel as any)[key] = cachedAppModel[key]; // TODO fix in package
			if (this._main.model.hasOwnProperty(key)) {
				(this._main.model as any)[key] = cachedAppModel[key]; // TODO fix in package
			}
		}

		const cachedBindedModel = JSON.parse(cacheData.bindedModel!);
		for (const key in cachedBindedModel) {
			if (bindedModel.hasOwnProperty(key)) {
				(bindedModel as any)[key] = cachedBindedModel[key]; // TODO fix in package
			}
		}
	}
}
